import React, { Component } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip } from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

import { GET, loading, NavbarAdmin, t } from "../../components/CustomComponent.js";
import { withRouter } from "../../components/withRouter.js";
import {
  BRAND,
  STATUS,
  STATUS_TINT,
  TYPE_RAMP,
  num,
  share,
  moneyShort,
  fmtRange,
  fmtStamp,
  presetRange,
  rangeFromSearch,
  toISODate,
  localName,
  maxOf,
  Figure,
  Chip,
  Bar as BarLine,
  StackBar,
  Card,
  Section,
  CardTitle,
  KpiTile,
  PeriodFilter,
  ReportHero,
  PERIODS,
} from "../../components/report/ReportKit.js";

import "../../assets/css/dashboard.css";
import "../../assets/css/report.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip);
ChartJS.defaults.font.family = "FCIconic, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/* ============================================================================
 * รายงานทรัพย์สิน  (/ad/reports/property)
 *
 * ข้อมูลทั้งหน้ามาจาก GET admin/reports/property?start=YYYY-MM-DD&end=YYYY-MM-DD
 *
 * summary / by_type / by_zone / price_bands / aging เป็นสต๊อก ณ ปัจจุบัน
 * ไม่ขึ้นกับตัวกรองช่วงเวลา ส่วน movement เป็นความเคลื่อนไหวเฉพาะช่วงที่เลือก
 *
 * หน้านี้เป็น "หน้าสรุป" ล้วน — ทุกบล็อกตอบคำถามระดับพอร์ต (มีอะไรอยู่เท่าไร
 * กระจุกที่ประเภทไหน ทำเลไหน ราคาช่วงไหน ค้างมานานแค่ไหน) ไม่มีตารางรายทรัพย์
 * เพราะการไล่ดูทีละรายการเป็นงานคนละอย่างกัน และเมนู "ทรัพย์สิน" ทำได้ดีกว่าอยู่แล้ว
 * ด้วยตัวกรอง การค้นหา และการแบ่งหน้าที่ครบกว่า — วางตารางย่อยไว้ท้ายรายงานอีกชุด
 * มีแต่ทำให้หน้ายาวขึ้นโดยที่ยังตอบได้ไม่ครบเท่าหน้านั้น
 * ========================================================================== */

/* โครงเปล่าที่ใช้ระหว่างรอ API ตอบ — ทุกส่วนของหน้าจึง render ได้โดยไม่ต้องเช็ก null ทีละจุด */
const EMPTY_DATA = {
  summary: { total: 0, available: 0, reserved: 0, closed: 0, off_market: 0, portfolio_value: 0, avg_price: 0, avg_days: 0 },
  by_type: [],
  by_zone: [],
  price_bands: [],
  aging: [],
  zone_count: 0,
  movement: { new_listings: 0, sold: 0, views: 0, delta_new: 0, delta_sold: 0, delta_views: 0 },
};

/* ========================================================================== */

/* ป้ายของ "ช่องสถานะ" ในรายงาน — ยุบสถานะจริง 6 ค่าเหลือ 4 ช่อง
   ทรัพย์ 1 รายการอยู่ได้ช่องเดียวเสมอ ทั้งสี่ช่องรวมกันจึงเท่ากับ total พอดี
   (สถานะจริงรายทรัพย์ดูได้ที่เมนูทรัพย์สิน ซึ่งใช้ statusMeta() ของชุดกลาง) */
const STATUS_LABEL = { available: "status_available", reserved: "status_reserved", closed: "closed_deals", off_market: "status_off_market" };

class ReportProperty extends Component {
  constructor(props) {
    super(props);
    // เปิดจากปุ่ม "ดูรายงานเต็ม" ในแดชบอร์ดจะมีช่วงเวลาติดมากับ url
    // จะได้เห็นช่วงเดียวกับที่กรองไว้ ไม่ใช่เด้งกลับไปเป็นเดือนนี้เสมอ
    const initial = rangeFromSearch(props.router?.location?.search) || { period: "month", ...presetRange("month") };
    this.state = {
      period: initial.period,
      start: initial.start,
      end: initial.end,
      data: EMPTY_DATA,
      stamp: fmtStamp(),
    };
  }

  async componentDidMount() {
    await this.getData();
  }

  getData = async () => {
    const { start, end } = this.state;

    loading(true);
    const result = await GET("admin/reports/property", {
      start: toISODate(start),
      end: toISODate(end || start),
    });
    loading(false);

    if (!result?.status || !result.data) return;

    // เติมคีย์ที่ขาดด้วยโครงเปล่า เผื่อ API เวอร์ชันเก่ายังไม่ส่งบางส่วนมา
    this.setState({ data: { ...EMPTY_DATA, ...result.data }, stamp: fmtStamp() });
  };

  changeRange = (next) => {
    // ยังเลือกวันสิ้นสุดไม่ครบ = ยังไม่ต้องยิงข้อมูลใหม่
    this.setState(next, () => (next.end ? this.getData() : null));
  };

  periodShort = () => {
    const p = PERIODS.find((x) => x.key === this.state.period);
    return p ? t(p.label) : t("custom_range");
  };

  rangeText = () => fmtRange(this.state.start, this.state.end);

  /* --------------------------------------------------------------- ส่วนย่อย */

  renderKpis() {
    const { summary, movement } = this.state.data;
    return (
      <div className="row g-3">
        <div className="col-xl-2 col-lg-4 col-md-6 dash-in">
          <KpiTile
            label={t("total_properties")}
            value={<Figure value={summary.total} />}
            unit={t("unit_items")}
            icon={""}
            foot={
              <StackBar
                segments={[
                  { label: t("status_available"), value: summary.available, color: STATUS.available },
                  { label: t("status_reserved"), value: summary.reserved, color: STATUS.reserved },
                  { label: t("closed_deals"), value: summary.closed, color: STATUS.closed },
                  { label: t("status_off_market"), value: summary.off_market, color: STATUS.off_market },
                ]}
                height={7}
              />
            }
          />
        </div>

        {[
          { key: "available", label: t("status_available"), icon: "" },
          { key: "reserved", label: t("status_reserved"), icon: "" },
          { key: "closed", label: t("closed_deals"), icon: "" },
        ].map((s) => (
          <div className="col-xl-2 col-lg-4 col-md-6 dash-in" key={s.key}>
            <KpiTile
              label={s.label}
              value={<Figure value={summary[s.key]} />}
              unit={t("unit_items")}
              icon={s.icon}
              color={STATUS[s.key]}
              tint={STATUS_TINT[s.key]}
              foot={
                <>
                  <BarLine value={summary[s.key]} max={summary.total} color={STATUS[s.key]} height={5} />
                  <div className="text-10 text-secondary mt-2">
                    {share(summary[s.key], summary.total)}% {t("of_portfolio")}
                  </div>
                </>
              }
            />
          </div>
        ))}

        <div className="col-xl-2 col-lg-6 col-md-6 dash-in">
          <KpiTile
            label={t("portfolio_value")}
            value={moneyShort(summary.portfolio_value)}
            icon={""}
            foot={<div className="text-10 text-secondary">{t("avg_price")} {moneyShort(summary.avg_price)}</div>}
          />
        </div>

        <div className="col-xl-2 col-lg-6 col-md-6 dash-in">
          <KpiTile
            label={t("new_listings")}
            value={<Figure value={movement.new_listings} />}
            unit={t("unit_items")}
            icon={""}
            delta={movement.delta_new}
            foot={
              <div className="d-flex align-items-center justify-content-between gap-2">
                <span className="badge rounded-pill fw-semibold text-10 px-2 py-1" style={{ backgroundColor: BRAND.limeSoft, color: BRAND.ink }}>
                  {this.periodShort()}
                </span>
                <span className="text-10 text-secondary">
                  {t("sold_in_period")} <b className="text-dark">{num(movement.sold)}</b>
                </span>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  renderTypeDonut() {
    const { by_type, summary } = this.state.data;
    const sorted = [...by_type].sort((a, b) => b.total - a.total);
    const data = {
      labels: sorted.map((x) => localName(x)),
      datasets: [
        {
          data: sorted.map((x) => x.total),
          backgroundColor: sorted.map((_, i) => TYPE_RAMP[i % TYPE_RAMP.length]),
          borderColor: "#fff",
          borderWidth: 2,
          hoverOffset: 10,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      animation: { animateRotate: true, duration: 900 },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: BRAND.ink,
          padding: 12,
          cornerRadius: 10,
          displayColors: false,
          titleFont: { size: 12, weight: "normal" },
          bodyFont: { size: 14, weight: "bold" },
          callbacks: {
            label: (c) => {
              const tot = c.dataset.data.reduce((s, v) => s + v, 0);
              return `${num(c.parsed)} ${t("unit_items")} (${share(c.parsed, tot)}%)`;
            },
          },
        },
      },
    };

    return (
      <Card>
        <div className="p-4 d-flex flex-column h-100">
          <CardTitle title={t("properties_by_type")} note={t("as_of_now")} />
          <div className="position-relative mx-auto" style={{ width: "172px", height: "172px" }}>
            <Doughnut data={data} options={options} />
            <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ pointerEvents: "none" }}>
              <Figure value={summary.total} className="fw-bold text-dark d-block" style={{ fontSize: "27px" }} />
              <div className="text-10 text-secondary">{t("unit_items")}</div>
            </div>
          </div>
          <div className="mt-3">
            {sorted.map((x, i) => (
              <div className="dash-rank d-flex align-items-center justify-content-between py-1 px-2" key={x.uid || x.name_th}>
                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                  <span className="rounded-circle me-2 flex-shrink-0" style={{ width: "9px", height: "9px", backgroundColor: TYPE_RAMP[i % TYPE_RAMP.length] }} />
                  <span className="text-12 text-dark text-truncate">{localName(x)}</span>
                </div>
                <div className="text-nowrap ms-2">
                  <span className="text-12 fw-bold text-dark dash-figure">{num(x.total)}</span>
                  <span className="text-10 text-secondary ms-2" style={{ display: "inline-block", width: "34px", textAlign: "right" }}>
                    {share(x.total, summary.total)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  renderStatusByType() {
    const { by_type } = this.state.data;
    const sorted = [...by_type].sort((a, b) => b.total - a.total);
    const mk = (key, color) => ({
      label: t(STATUS_LABEL[key]),
      data: sorted.map((x) => x[key]),
      backgroundColor: color,
      borderRadius: 4,
      borderSkipped: false,
      barThickness: 16,
    });

    const data = {
      labels: sorted.map((x) => localName(x)),
      datasets: [
        mk("available", STATUS.available),
        mk("reserved", STATUS.reserved),
        mk("closed", STATUS.closed),
        mk("off_market", STATUS.off_market),
      ],
    };

    const options = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 800, easing: "easeOutQuart" },
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(20,32,22,.96)",
          padding: 12,
          cornerRadius: 10,
          boxPadding: 4,
          titleFont: { size: 12 },
          bodyFont: { size: 13, weight: "bold" },
          callbacks: { label: (c) => ` ${c.dataset.label}  ${num(c.parsed.x)}` },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { color: "rgba(20,32,22,.06)", drawTicks: false },
          border: { display: false },
          ticks: { color: BRAND.muted, font: { size: 10 }, precision: 0, maxTicksLimit: 6, padding: 6 },
        },
        y: {
          stacked: true,
          grid: { display: false },
          border: { display: false },
          ticks: { color: BRAND.ink, font: { size: 11 }, padding: 6 },
        },
      },
    };

    return (
      <Card>
        <div className="p-4 d-flex flex-column h-100">
          <CardTitle
            title={t("status_by_type")}
            note={t("as_of_now")}
            right={
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {["available", "reserved", "closed", "off_market"].map((k) => (
                  <span className="d-flex align-items-center gap-2" key={k}>
                    <span className="rounded-circle" style={{ width: "8px", height: "8px", backgroundColor: STATUS[k] }} />
                    <span className="text-10 text-secondary">{t(STATUS_LABEL[k])}</span>
                  </span>
                ))}
              </div>
            }
          />
          <div className="flex-grow-1" style={{ minHeight: "300px" }}>
            <Bar data={data} options={options} />
          </div>
        </div>
      </Card>
    );
  }

  renderPriceBands() {
    const { price_bands } = this.state.data;
    const data = {
      labels: price_bands.map((x) => x.label),
      datasets: [
        {
          data: price_bands.map((x) => x.count),
          backgroundColor: (ctx) => {
            const { chart } = ctx;
            if (!chart.chartArea) return "rgba(163,230,53,.7)";
            const g = chart.ctx.createLinearGradient(0, chart.chartArea.bottom, 0, chart.chartArea.top);
            g.addColorStop(0, "rgba(163,230,53,.45)");
            g.addColorStop(1, "rgba(77,124,15,.95)");
            return g;
          },
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 44,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 800, easing: "easeOutQuart" },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(20,32,22,.96)",
          padding: 12,
          cornerRadius: 10,
          displayColors: false,
          titleFont: { size: 11, weight: "normal" },
          bodyFont: { size: 15, weight: "bold" },
          callbacks: {
            title: (c) => `${c[0].label} ${t("unit_million")}`,
            label: (c) => `${num(c.parsed.y)} ${t("unit_items")}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: BRAND.muted, font: { size: 11 }, padding: 4 },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(20,32,22,.06)", drawTicks: false },
          border: { display: false },
          ticks: { color: BRAND.muted, font: { size: 10 }, precision: 0, maxTicksLimit: 5, padding: 8 },
        },
      },
    };

    // ระหว่างรอ API ตอบ price_bands ยังเป็นรายการว่าง จึงต้องกันไว้ก่อนหยิบตัวแรกมาใช้
    const top = [...price_bands].sort((a, b) => b.count - a.count)[0];
    const total = price_bands.reduce((s, x) => s + x.count, 0);

    return (
      <Card>
        <div className="p-4 d-flex flex-column h-100">
          <CardTitle title={t("price_distribution")} note={`${t("unit_baht")} · ${t("unit_million")}`} />
          <div className="flex-grow-1" style={{ minHeight: "215px" }}>
            <Bar data={data} options={options} />
          </div>
          <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${BRAND.line}` }}>
            <div className="d-flex align-items-center justify-content-between">
              <span className="text-12 text-secondary">{t("most_common_band")}</span>
              <span className="badge rounded-pill text-12 fw-bold px-3 py-2" style={{ backgroundColor: BRAND.limeSoft, color: BRAND.ink }}>
                {top ? `${top.label} ${t("unit_million")} · ${share(top.count, total)}%` : "-"}
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  renderAging() {
    const { aging } = this.state.data;
    const total = aging.reduce((s, x) => s + x.count, 0);
    const stale = aging.slice(2).reduce((s, x) => s + x.count, 0);
    return (
      <Card>
        <div className="p-4 d-flex flex-column h-100">
          <CardTitle title={t("listing_aging")} note={t("aging_desc")} />
          <div className="d-flex flex-column gap-3">
            {aging.map((a) => (
              <div key={a.key}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-flex align-items-center gap-2">
                    <span className="rounded-circle" style={{ width: "8px", height: "8px", backgroundColor: a.color }} />
                    <span className="text-12 text-dark">{t(a.key)}</span>
                  </span>
                  <span className="text-nowrap">
                    <span className="text-14 fw-bold text-dark dash-figure">{num(a.count)}</span>
                    <span className="text-10 text-secondary ms-2" style={{ display: "inline-block", width: "34px", textAlign: "right" }}>
                      {share(a.count, total)}%
                    </span>
                  </span>
                </div>
                <BarLine value={a.count} max={total} color={a.color} height={6} />
              </div>
            ))}
          </div>
          <div className="mt-auto pt-3">
            <div className="rounded-3 p-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: "#fff5f5", border: "1px solid #fbd5d5" }}>
              <span className="text-12 fw-semibold" style={{ color: "#b91c1c" }}>
                <span className="icon me-2 text-12">{""}</span>
                {t("needs_attention")}
              </span>
              <span className="text-14 fw-bold" style={{ color: "#b91c1c" }}>
                {num(stale)} {t("unit_items")}
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  /* ตารางสรุปตามทำเล — เหลือคอลัมน์ที่ตอบคำถามของหน้าสรุปจริง ๆ เท่านั้น
     ของเดิมมีทั้งแถบสัดส่วนสถานะ "และ" ตัวเลขของแต่ละสถานะแยกอีกสามคอลัมน์ ซึ่งเป็น
     ข้อมูลชุดเดียวกันเล่าสองรอบ กินความกว้างจนตารางต้องเลื่อนแนวนอนบนจอปกติ
     แถบสัดส่วนตอบ "กระจายตัวยังไง" ได้ในพริบตาอยู่แล้ว ส่วนตัวเลขรายสถานะของทำเล
     หนึ่ง ๆ เป็นคำถามระดับรายการ ซึ่งเมนูทรัพย์สินตอบได้ตรงกว่า

     คงไว้ : ชื่อทำเล · จำนวน · สัดส่วนสถานะ · มูลค่า · อัตราว่าง (ตัวชี้วัดของทำเล) */
  renderZoneTable() {
    const { by_zone, zone_count } = this.state.data;
    const max = maxOf(by_zone, (x) => x.total);
    const sum = by_zone.reduce(
      (a, r) => ({ total: a.total + r.total, available: a.available + r.available, value: a.value + r.value }),
      { total: 0, available: 0, value: 0 }
    );

    /* backend ตัดมาให้เฉพาะทำเลต้น ๆ (ZONE_LIMIT) — บอกไปตรง ๆ ว่ากำลังดูกี่จาก
       ทั้งหมดเท่าไร ไม่ใช่ตัดเงียบ ๆ จนอ่านเหมือนบริษัทมีทรัพย์อยู่แค่ในทำเลเหล่านี้
       (แถวท้ายตารางจึงเป็นยอดของ "ทำเลที่แสดง" ไม่ใช่ของทั้งพอร์ต — เขียนกำกับไว้) */
    const trimmed = zone_count > by_zone.length;

    return (
      <Card>
        <div className="p-4">
          <CardTitle
            title={t("summary_by_zone")}
            note={trimmed ? `${t("as_of_now")} · ${t("top_zones")} ${num(by_zone.length)}/${num(zone_count)}` : t("as_of_now")}
            right={<Chip icon={""} size={32} />}
          />
          <div className="rpt-scroll">
            <table className="rpt-table">
              <thead>
                <tr>
                  <th style={{ minWidth: "190px" }}>{t("zone")}</th>
                  <th className="num">{t("total")}</th>
                  <th style={{ minWidth: "180px" }}>{t("status_ratio")}</th>
                  <th className="num">{t("portfolio_value")}</th>
                  <th className="num" style={{ width: "110px" }}>{t("vacancy_rate")}</th>
                </tr>
              </thead>
              <tbody>
                {by_zone.map((r) => (
                  <tr key={r.name_th}>
                    <td>
                      <div className="text-13 text-dark">{localName(r)}</div>
                      <div style={{ width: "96px", marginTop: "5px" }}>
                        <BarLine value={r.total} max={max} color={BRAND.ink} height={4} />
                      </div>
                    </td>
                    <td className="num fw-bold">{num(r.total)}</td>
                    <td>
                      <StackBar
                        segments={[
                          { label: t("status_available"), value: r.available, color: STATUS.available },
                          { label: t("status_reserved"), value: r.reserved, color: STATUS.reserved },
                          { label: t("closed_deals"), value: r.closed, color: STATUS.closed },
                          { label: t("status_off_market"), value: r.off_market, color: STATUS.off_market },
                        ]}
                        height={7}
                      />
                    </td>
                    <td className="num">{moneyShort(r.value)}</td>
                    <td className="num fw-bold">{share(r.available, r.total)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{trimmed ? t("total_shown") : t("total")}</td>
                  <td className="num">{num(sum.total)}</td>
                  <td />
                  <td className="num">{moneyShort(sum.value)}</td>
                  <td className="num">{share(sum.available, sum.total)}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </Card>
    );
  }

  /* ---------------------------------------------------------------- render */

  render() {
    const { period, start, end, data, stamp } = this.state;
    const S = data.summary;

    return (
      <div className="w-100 min-vh-100 dash-page">
        <NavbarAdmin />
        <div className="body p-4">
          <ReportHero
            icon={""}
            title={t("property_report")}
            subtitle={t("property_report_desc")}
            rangeText={this.rangeText()}
            headline={[
              { label: t("total_properties"), value: num(S.total), unit: t("unit_items") },
              { label: t("portfolio_value"), value: moneyShort(S.portfolio_value) },
              { label: t("avg_days_on_market"), value: num(S.avg_days), unit: t("unit_days") },
            ]}
          />

          <div className="rpt-toolbar rpt-no-print">
            <PeriodFilter value={{ period, start, end }} onChange={this.changeRange} />
            <span className="text-12 text-secondary">
              <span className="icon me-2 text-12">{""}</span>
              {t("report_generated_at")} {stamp}
            </span>
          </div>

          <Section title={t("section_portfolio_overview")} note={t("as_of_now")} />
          {this.renderKpis()}

          <Section title={t("section_breakdown")} note={t("by_type_and_price")} />
          <div className="row g-3">
            <div className="col-xl-4 col-lg-6 dash-in">{this.renderTypeDonut()}</div>
            <div className="col-xl-8 col-lg-6 dash-in">{this.renderStatusByType()}</div>
            <div className="col-xl-7 col-lg-7 dash-in">{this.renderPriceBands()}</div>
            <div className="col-xl-5 col-lg-5 dash-in">{this.renderAging()}</div>
          </div>

          <Section title={t("section_by_zone")} />
          <div className="row g-3 pb-4">
            <div className="col-12 dash-in">{this.renderZoneTable()}</div>
          </div>

          <div className="text-center text-10 text-secondary pb-4">
            {t("report_footer_note")}
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(ReportProperty);
